import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Bot, BookOpen, Users, Users2, Headphones, Radio, BarChart2,
  CreditCard, MessageSquare, Megaphone, Shield, ScrollText, Zap,
  Gift, TrendingUp, ChevronLeft, LayoutDashboard, Send
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme, focusRing } from '../styles/theme';
import logoWhiteOrange from '../assets/branco_laranja.png';
import api from '../services/api';

const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED_WIDTH = 56;
const STORAGE_KEY = 'sidebar_collapsed';

// Estrutura agrupada para business — reduz percepção de 16 itens para 5 grupos
function buildGroups(t, tenantId, role, isPersonal) {
  const p = (path) => `/${path}/${tenantId}`;

  if (isPersonal) {
    return [
      {
        items: [
          { key: 'dashboard', icon: LayoutDashboard, label: t('nav.dashboard',           { defaultValue: 'Início' }),          path: p('dashboard') },
          { key: 'agents',    icon: Bot,             label: t('personal.myAgent',         { defaultValue: 'Meu Assistente' }), path: p('agents-config') },
          { key: 'logs',      icon: Send,            label: t('personal.conversations',   { defaultValue: 'Conversas' }),      path: p('logs') },
          { key: 'knowledge', icon: BookOpen,        label: t('personal.knowledge',       { defaultValue: 'O que ele sabe' }), path: p('knowledge') },
          { key: 'tasks',     icon: Zap,             label: t('automations.title', { defaultValue: 'Automações' }),        path: p('automations') },
          { key: 'billing',   icon: CreditCard,      label: t('nav.billing'),                                                  path: p('billing') },
          { key: 'referral',  icon: Gift,            label: t('nav.referral',             { defaultValue: 'Indique e Ganhe' }),path: p('referral') },
        ],
      },
    ];
  }

  const allGroups = [
    {
      label: t('nav.group.operation', { defaultValue: 'Operação' }),
      items: [
        { key: 'dashboard',   icon: LayoutDashboard, label: t('nav.dashboard',    { defaultValue: 'Início' }),   path: p('dashboard') },
        { key: 'callcenter',  icon: Headphones,      label: t('nav.callCenter'),                                  path: p('callcenter') },
        { key: 'monitor',     icon: Radio,           label: t('nav.monitor'),                                     path: p('monitor') },
        { key: 'logs',        icon: ScrollText,      label: t('nav.logs'),                                        path: p('logs') },
      ],
    },
    {
      label: t('nav.group.config', { defaultValue: 'Configuração' }),
      items: [
        { key: 'agents',    icon: Bot,      label: t('nav.agents'),     path: p('agents-config') },
        { key: 'team',      icon: Users2,   label: t('nav.team'),       path: p('team') },
        { key: 'users',     icon: Users,    label: t('nav.users'),      path: p('users') },
        { key: 'knowledge', icon: BookOpen, label: t('nav.knowledge'),  path: p('knowledge') },
      ],
    },
    {
      label: t('nav.group.marketing', { defaultValue: 'Marketing' }),
      items: [
        { key: 'campaigns', icon: Megaphone,     label: t('campaigns.title', { ns: 'common' }), path: p('campaigns') },
        { key: 'templates', icon: MessageSquare, label: t('nav.templates'),                      path: p('templates') },
        { key: 'mktpl',     icon: Send,          label: t('nav.mktpl', { defaultValue: 'Templates de Marketing' }), path: p('mktpl') },
        { key: 'tasks',     icon: Zap,           label: t('nav.tasks'),                          path: p('tasks') },
      ],
    },
    {
      label: t('nav.group.analysis', { defaultValue: 'Análise' }),
      items: [
        { key: 'manager',         icon: BarChart2,  label: t('nav.managerDashboard'),                                       path: p('manager') },
        { key: 'agents-activity', icon: TrendingUp, label: t('nav.agentsActivity', { defaultValue: 'Atividade' }),          path: p('agents-activity') },
      ],
    },
    {
      label: t('nav.group.account', { defaultValue: 'Conta' }),
      items: [
        { key: 'billing',  icon: CreditCard, label: t('nav.billing'),                                            path: p('billing') },
        { key: 'audit',    icon: Shield,     label: t('audit.title', { ns: 'common' }),                          path: p('audit') },
        { key: 'referral', icon: Gift,       label: t('nav.referral', { defaultValue: 'Indique e Ganhe' }),      path: p('referral') },
      ],
    },
  ];

  const roleFilter = {
    agent:        ['dashboard', 'callcenter'],
    manager:      ['dashboard', 'callcenter', 'monitor', 'logs', 'agents', 'campaigns', 'templates', 'mktpl', 'tasks', 'team', 'manager', 'agents-activity', 'billing'],
    admin:        null,
    tenant_admin: null,
    sallus_admin: null,
  };

  const allowed = roleFilter[role];
  if (!allowed) return allGroups;

  return allGroups
    .map(g => ({ ...g, items: g.items.filter(i => allowed.includes(i.key)) }))
    .filter(g => g.items.length > 0);
}

// Hook de badges — polling a cada 30s, só para roles com acesso
function useSidebarBadges(tenantId, role, isPersonal) {
  const [badges, setBadges] = useState({ callcenter: 0, agents: 0, billing: false });

  const fetch = useCallback(async () => {
    if (!tenantId || isPersonal) return;
    const canSeeQueue   = ['admin', 'tenant_admin', 'manager', 'agent'].includes(role);
    const canSeeAgents  = ['admin', 'tenant_admin'].includes(role);
    const canSeeBilling = ['admin', 'tenant_admin'].includes(role);

    const [queueRes, errorsRes, tenantRes] = await Promise.all([
      canSeeQueue   ? api.get(`/conversations/${tenantId}/queue`).catch(() => null)  : Promise.resolve(null),
      canSeeAgents  ? api.get(`/marketplace/tenants/${tenantId}/errors`).catch(() => null) : Promise.resolve(null),
      canSeeBilling ? api.get(`/tenants/${tenantId}`).catch(() => null) : Promise.resolve(null),
    ]);

    const queueCount  = queueRes?.data?.queue?.length ?? 0;
    const errorsCount = (errorsRes?.data?.errors || []).filter(e => !e.resolved).length;

    let billingAlert = false;
    if (tenantRes?.data) {
      const t = tenantRes.data;
      const isOverdue   = t.status === 'suspended' || t.billingStatus === 'overdue';
      const trialEnding = t.status === 'trial' && t.trialEndsAt &&
        (new Date(t.trialEndsAt) - Date.now()) < 3 * 24 * 60 * 60 * 1000; // < 3 dias
      billingAlert = isOverdue || trialEnding;
    }

    setBadges({ callcenter: queueCount, agents: errorsCount, billing: billingAlert });
  }, [tenantId, role, isPersonal]);

  useEffect(() => {
    fetch();
    const id = setInterval(fetch, 30000);
    return () => clearInterval(id);
  }, [fetch]);

  return badges;
}

export default function Sidebar({ tenantId, role, isPersonal }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const badges = useSidebarBadges(tenantId, role, isPersonal);

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setMobileOpen(v => !v);
    window.addEventListener('sidebar:toggle', handler);
    return () => window.removeEventListener('sidebar:toggle', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const toggle = useCallback(() => {
    setCollapsed(v => {
      const next = !v;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  useEffect(() => {
    const w = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
    document.documentElement.style.setProperty('--sidebar-width', `${w}px`);
  }, [collapsed]);

  useEffect(() => {
    const w = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
    document.documentElement.style.setProperty('--sidebar-width', `${w}px`);
    return () => document.documentElement.style.removeProperty('--sidebar-width');
  }, []); // eslint-disable-line

  const groups = buildGroups(t, tenantId, role, isPersonal);
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Mapeia key do item para badge
  const badgeMap = {
    callcenter: badges.callcenter > 0 ? badges.callcenter : null,
    agents:     badges.agents > 0     ? badges.agents     : null,
    billing:    badges.billing        ? '!' : null,
  };

  return (
    <>
      {mobileOpen && <Overlay onClick={() => setMobileOpen(false)} />}

      <Nav $collapsed={collapsed} $mobileOpen={mobileOpen}>
        <LogoRow $collapsed={collapsed}>
          {!collapsed && <LogoImg src={logoWhiteOrange} alt="Libben" />}
          <CollapseBtn onClick={toggle} title={collapsed ? t('expand', { defaultValue: 'Expandir' }) : t('collapse', { defaultValue: 'Recolher' })}>
            <ChevronLeft size={16} style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </CollapseBtn>
        </LogoRow>

        <NavList>
          {groups.map((group, gi) => (
            <NavGroup key={gi}>
              {/* Label do grupo — oculto quando colapsado */}
              {!collapsed && group.label && (
                <GroupLabel>{group.label}</GroupLabel>
              )}
              {/* Separador quando colapsado */}
              {collapsed && gi > 0 && <GroupDivider />}

              {group.items.map(({ key, icon: Icon, label, path }) => {
                const active = isActive(path);
                const badge  = badgeMap[key];
                return (
                  <NavItem
                    key={key}
                    $active={active}
                    $collapsed={collapsed}
                    onClick={() => navigate(path)}
                    title={collapsed ? label : undefined}
                    aria-label={label}
                    aria-current={active ? 'page' : undefined}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && navigate(path)}
                  >
                    <NavIcon $active={active}>
                      <Icon size={18} strokeWidth={1.6} />
                      {collapsed && badge && (
                        <CollapsedBadge $dot={badge === '!'}>
                          {badge === '!' ? '' : badge > 99 ? '99+' : badge}
                        </CollapsedBadge>
                      )}
                    </NavIcon>
                    {!collapsed && <NavLabel $active={active}>{label}</NavLabel>}
                    {!collapsed && badge && (
                      <NavBadge $dot={badge === '!'}>
                        {badge === '!' ? '' : badge > 99 ? '99+' : badge}
                      </NavBadge>
                    )}
                    {active && <ActiveBar />}
                  </NavItem>
                );
              })}
            </NavGroup>
          ))}
        </NavList>
      </Nav>
    </>
  );
}

const Nav = styled.nav`
  position: fixed;
  top: 64px;
  left: 0;
  bottom: 0;
  width: ${p => p.$collapsed ? `${SIDEBAR_COLLAPSED_WIDTH}px` : `${SIDEBAR_WIDTH}px`};
  background: ${theme.colors.white};
  border-right: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  z-index: ${theme.zIndex.sticky - 1};
  transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  @media (max-width: 768px) {
    top: 64px;
    width: ${SIDEBAR_WIDTH}px;
    transform: translateX(${p => p.$mobileOpen ? '0' : `-${SIDEBAR_WIDTH}px`});
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: ${p => p.$mobileOpen ? theme.shadows.xl : 'none'};
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  z-index: ${theme.zIndex.sticky - 2};
  @media (min-width: 769px) { display: none; }
`;

const LogoRow = styled.div`
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: ${p => p.$collapsed ? 'center' : 'space-between'};
  padding: 0 ${p => p.$collapsed ? '0' : theme.spacing[3]};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const LogoImg = styled.img`
  height: 28px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
`;

const CollapseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.gray50};
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primaryLight}; color: ${theme.colors.primary}; border-color: ${theme.colors.primaryBorder}; }
`;

const NavList = styled.ul`
  list-style: none;
  padding: ${theme.spacing[2]} 0 ${theme.spacing[4]};
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
`;

const NavGroup = styled.li`
  list-style: none;
  & + & { margin-top: ${theme.spacing[1]}; }
`;

const GroupLabel = styled.div`
  padding: ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[1]};
  font-size: 10px;
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  user-select: none;
`;

const GroupDivider = styled.div`
  height: 1px;
  background: ${theme.colors.border};
  margin: ${theme.spacing[2]} ${theme.spacing[2]};
`;

const NavItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${p => p.$collapsed ? '0' : theme.spacing[3]};
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  margin: 1px ${theme.spacing[2]};
  border-radius: ${theme.radii.md};
  cursor: pointer;
  background: ${p => p.$active ? theme.colors.primaryLight : 'transparent'};
  transition: ${theme.transitions.fast};
  &:hover { background: ${p => p.$active ? theme.colors.primaryLight : theme.colors.gray100}; }
  ${focusRing}
`;

const NavIcon = styled.span`
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textMuted};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  position: relative;
`;

const NavLabel = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${p => p.$active ? theme.typography.semibold : theme.typography.normal};
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActiveBar = styled.span`
  position: absolute;
  left: -${theme.spacing[2]};
  top: 20%;
  bottom: 20%;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: ${theme.colors.primary};
`;

const NavBadge = styled.span`
  margin-left: auto;
  min-width: ${p => p.$dot ? '8px' : '18px'};
  height: ${p => p.$dot ? '8px' : '18px'};
  border-radius: ${p => p.$dot ? '50%' : '9px'};
  background: ${p => p.$dot ? theme.colors.danger : theme.colors.danger};
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${p => p.$dot ? '0' : '0 5px'};
  flex-shrink: 0;
`;

const CollapsedBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: ${p => p.$dot ? '8px' : '16px'};
  height: ${p => p.$dot ? '8px' : '16px'};
  border-radius: ${p => p.$dot ? '50%' : '8px'};
  background: ${theme.colors.danger};
  color: white;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${p => p.$dot ? '0' : '0 4px'};
  border: 2px solid white;
`;
